import React, {
  useMemo,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from 'react';
import ArrTileSelection, {
  getSelectedTotal,
} from '../../../../common/arr-tile-selection/ArrTileSelection';
import './SearchTermReportTotalCount.scss';
import {ReactComponent as ForwardIcon} from '../../../../../assets/images/forward.svg';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {STR_Action_Menu_List} from '../../../../../utils/reportUtils';
import ActionMenuWrapper from '../../../../action-menu/action-menu-wrapper/ActionMenuWrapper';
import {
  ReportActionMenuOptionTypes,
  SourceTypeCode,
  SourceTypeContextCode,
} from '../../../../../shared/constants';
import {DocActionMenuItem} from '../../../../../shared/interfaces';
import {t} from 'i18next';
import {ActionMenuPayloadType} from './SearchTermReportContainer';
import {CustomMenuItem} from '../../../../common/arr-menu/ArrMenu';
import ArrDocMaxLimitWarning from '../../../../common/arr-doc-max-limit-warning/ArrDocMaxLimitWarning';
import {
  clearDocViewState,
  createDocumentViewerListIdRequestPayload,
} from '../../../../document-viewer/docViewerUtils';
import {useDocumentViewerListId} from '../../../../../hooks/document/useDocument';
import {
  storeDocumentListMasterId,
  storeSourceTypeCode,
} from '../../../../../redux/slice/docViewSlice';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {useNavigate} from 'react-router-dom';
import {
  setDocListBackNavigation,
  setDocListId,
} from '../../../../../redux/slice/docListSlice';

export interface Tile {
  key: string;
  label: string;
  total: number;
  selected: boolean;
  disabled?: boolean;
}

interface Props {
  data?: Record<string, number>;
  tiles?: Tile[];
  actions?: CustomMenuItem[];
  onTileChange?: (key: string, selected: boolean) => void;
  className?: string;
  'data-testid'?: string;
  totalSelectedCount?: number;
  actionMenuPayload?: ActionMenuPayloadType;
  onActionSelect?: (
    action: CustomMenuItem,
    callBackForDoc?: (docId: string, source: string) => void
  ) => void;
}

type TileAction =
  | {type: 'toggle'; key: string; selected: boolean}
  | {type: 'set_all'; tiles: Tile[]};

function tilesReducer(state: Tile[], action: TileAction): Tile[] {
  switch (action.type) {
    case 'toggle':
      return state.map((t) =>
        t.key === action.key ? {...t, selected: action.selected} : t
      );
    case 'set_all':
      return action.tiles;
    default:
      return state;
  }
}

// Mapping API keys to labels
const tileMapping: Record<string, string> = {
  pureHits: 'Hits',
  families: 'Family Members',
  nearDupes: 'Near Dupes',
  emailThreads: 'Email Threaded',
  uniqueHits: 'Unique Hits',
};

// Generate tiles dynamically from API data
const generateTilesFromData = (data?: Record<string, number>): Tile[] => {
  const safeData = data ?? {};
  return Object.entries(tileMapping).reduce<Tile[]>((acc, [key, label]) => {
    const hasKey = Object.prototype.hasOwnProperty.call(safeData, key);
    if (hasKey) {
      // eslint-disable-next-line security/detect-object-injection
      const val = safeData[key] ?? 0;
      acc.push({
        key,
        label,
        total: val,
        selected: false,
        disabled: val === 0,
      });
    }
    return acc;
  }, []);
};

type LstBulkActionContextType = {
  ID: string;
  IncludePureHits: boolean;
  IncludeFamily: boolean;
  IncludeEmailThread: boolean;
  IncludeNearDupe: boolean;
  IncludeConcept: boolean;
  IncludeUniquePureHits: boolean;
  IncludeUniqueFamilyMembers: boolean;
};

const SearchTermReportTotalCount: React.FC<Props> = ({
  data,
  tiles: initialTiles,
  onTileChange,
  className,
  totalSelectedCount,
  'data-testid': dataTestId = 'search-term-report-total-count',
  actionMenuPayload,
  onActionSelect,
}) => {
  const [tiles, dispatch] = useReducer(
    tilesReducer,
    initialTiles && initialTiles.length
      ? initialTiles
      : generateTilesFromData(data)
  );

  const [isActionBtnDisabled, setIsActionBtnDisabled] = useState(true);
  const [showDocLimitWarning, setShowDocLimitWarning] = useState(false);
  const [shouldOpenNewTab, setShouldOpenNewTab] = useState(false);
  const [navigationType, setNavigationType] = useState<SourceTypeCode>();
  const dispatchStore = useDispatch();
  const navigate = useNavigate();
  const loginUserData = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );
  const [docViewNavigationLoading, setDocViewNavigationLoading] =
    useState(false);

  useEffect(() => {
    const hasTileSelected = tiles.some((tile) => tile.selected);
    const hasSelected =
      typeof totalSelectedCount === 'number'
        ? totalSelectedCount > 0
        : hasTileSelected;
    setIsActionBtnDisabled(!hasSelected);
  }, [tiles, totalSelectedCount]);

  useEffect(() => {
    if (data) {
      dispatch({type: 'set_all', tiles: generateTilesFromData(data)});
    }
  }, [data]);
  const {mutate: fetchDocumentViewerListId} = useDocumentViewerListId();
  const handleTileToggle = useCallback(
    (key: string, selected: boolean) => {
      dispatch({type: 'toggle', key, selected});
      onTileChange?.(key, selected);
    },
    [onTileChange]
  );

  const totalSelected = useMemo(() => getSelectedTotal(tiles), [tiles]);
  const formattedTotal = useMemo(
    () => new Intl.NumberFormat('en-US').format(totalSelected),
    [totalSelected]
  );

  const toggleHandlers = useMemo(
    () =>
      Object.fromEntries(
        tiles.map((t) => [
          t.key,
          (selected: boolean) => handleTileToggle(t.key, selected),
        ])
      ),
    [tiles, handleTileToggle]
  );

  const actionMenuItems: CustomMenuItem[] = (() => {
    const userRole = String(
      loginUserData.roleId ?? loginUserData.baseRoleId ?? ''
    );
    const mapAndFilter = (items: DocActionMenuItem[]): CustomMenuItem[] =>
      items
        .filter((it) => !it.Roles || it.Roles.includes(userRole))
        .map((it) => ({
          id: String(it.id),
          label: t(`docExplorer.actionMenu.items.${it.name}`),
          disabled: it.disabled,
          submenu: it.subItems ? mapAndFilter(it.subItems) : undefined,
        }));
    return mapAndFilter(STR_Action_Menu_List);
  })();

  const handleActionSelect = (action: CustomMenuItem) => {
    if (!action) return;
    if (
      action.id === 'ViewInDocView' ||
      action.id === 'ViewInDocViewInNewTab'
    ) {
      setNavigationType(SourceTypeCode.DOCVIEW);
    } else if (action.id === 'ViewInDocList') {
      setNavigationType(SourceTypeCode.DOCLIST);
    }
    if (action.id === 'ViewInDocViewInNewTab') {
      setShouldOpenNewTab(true);
    }
    if (
      action.id.toLowerCase() === 'viewindocview' ||
      action.id.toLowerCase() === 'viewindocviewinnewtab' ||
      action.id.toLowerCase() === 'viewindoclist'
    ) {
      setShowDocLimitWarning(true);
    }
  };

  const startDocumentNavigation = (
    destinationContextTypeCode: SourceTypeCode | undefined,
    totalDocumentToView: number
  ) => {
    const requestPayload = createDocumentViewerListIdRequestPayload({
      sourceId: 0,
      sourceTypeCode: SourceTypeCode.SEARCHTERM,
      sourceDetailOverrides: {},
      docsToExcludeOverrides: {},
      sourceInfoOverrides: {
        sourceTypeCode: SourceTypeCode.SEARCHTERM,
        destinationContextTypeCode: destinationContextTypeCode,
        totalDocumentToView: totalDocumentToView,
        stR_SelectedSearchIds: actionMenuPayload?.stR_SelectedSearchIds,
        sourceDetails: actionMenuPayload?.sourceDetails,
      },
      docViewSessionOverrides: {
        assignmentSortDetails: [
          {
            sourceID: 0,
            columnManual: '',
            columnOptimized: '',
            columnIDsOptimized: '',
            columnOrderOptimized: '',
            columnIDsManual: '',
            columnOrderManual: '',
            showCompletedOptimized: true,
            showCompletedManual: true,
            columnDisplayManual: '',
            columnDataTypeOptimized: [],
            columnDataTypeManual: [],
          },
        ],
        docsForCodeSame: [],
      },
      sortConfigurationOverrides: {
        manualCols: '',
        showCompletedDocOptimized: true,
        showCompletedDocManual: true,
        configuredShowCompletedDoc: true,
      },
    });
    setDocViewNavigationLoading(true);
    fetchDocumentViewerListId(requestPayload, {
      onSuccess: (response) => {
        clearDocViewState();
        const masterId =
          response.documentListID ?? response.defaultMasterID ?? 0;
        setTimeout(() => {
          setDocViewNavigationLoading(false);
          dispatchStore(
            destinationContextTypeCode == SourceTypeCode.DOCVIEW
              ? storeDocumentListMasterId(String(masterId))
              : setDocListId(Number(masterId))
          );

          dispatchStore(storeSourceTypeCode(SourceTypeCode.SEARCHTERM));
          if (
            shouldOpenNewTab &&
            destinationContextTypeCode == SourceTypeCode.DOCVIEW
          ) {
            const newWindow = window.open('about:blank', '_blank');
            if (newWindow) {
              newWindow.location.href = `${window.location.origin}/DocumentViewer/DocView`;
            }
          } else {
            if (destinationContextTypeCode == SourceTypeCode.DOCLIST) {
              const reportId = actionMenuPayload?.reportId;
              const backPath = `/DataAnalysisReport/term/view?ReportID=${reportId}`;
              dispatchStore(
                setDocListBackNavigation({
                  label: t('docExplorer.header.searchTerm'),
                  path: backPath,
                })
              );
            }
            navigate(
              `${destinationContextTypeCode == SourceTypeCode.DOCVIEW ? '/DocumentViewer/DocView' : '/Document/DocList'}`
            );
          }
          setShowDocLimitWarning(false);
          setShouldOpenNewTab(false);
        }, 3000);
      },
    });
  };
  const getLstBulkActionContext = (sourceDetails: any[]) => {
    const sourceMap: {[key: string]: LstBulkActionContextType} = {};
    sourceDetails?.forEach((source: any) => {
      const sourceObj = sourceMap[source.s];
      if (sourceObj) {
        sourceObj.IncludeUniquePureHits = source.up || false;
      } else {
        sourceMap[source.s] = {
          ID: source.s,
          IncludePureHits: source.p || false,
          IncludeFamily: source.f || false,
          IncludeEmailThread: source.e || false,
          IncludeNearDupe: source.n || false,
          IncludeConcept: false,
          IncludeUniquePureHits: false,
          IncludeUniqueFamilyMembers: false,
        } as LstBulkActionContextType;
      }
    });
    return Object.values(sourceMap) ?? [];
  };

  return (
    <div
      className={`search-term-report-total-count ${className ?? ''}`}
      data-testid={dataTestId}
    >
      {/* Left Section: Tiles */}
      <div className="tiles-section" data-testid="tiles-section">
        {tiles.map((tile) => (
          <ArrTileSelection
            key={tile.key}
            label={tile.label}
            total={tile.total}
            selected={tile.selected}
            disabled={tile.disabled}
            ariaLabel={`Tile for ${tile.label}`}
            onToggle={toggleHandlers[tile.key]}
            dataTestId={`tile-${tile.key}`}
          />
        ))}
      </div>
      {/* Forward Icon */}
      <div
        className="forward-icon"
        data-testid="forward-icon"
        aria-hidden="true"
      >
        <ForwardIcon aria-label="Forward" />
      </div>
      {/* Right Section: Total + Action Button */}
      <div
        className="summary-section stack-on-mobile"
        data-testid="summary-section"
      >
        <div
          className="total-selected"
          aria-live="polite"
          data-testid="total-selected"
        >
          <span
            className="label"
            data-testid={`search-term-report-total-count-total-selected`}
          >
            Total Selected
          </span>
          <span className="value" data-testid="total-value">
            {totalSelectedCount ?? formattedTotal}
          </span>
        </div>

        <ActionMenuWrapper
          actions={actionMenuItems}
          sourceType={SourceTypeCode.SEARCHTERM}
          sourceDetails={{
            IsInverse: true,
            ExcludedLstBulkActionContext: [],
            LstBulkActionContext: getLstBulkActionContext(
              actionMenuPayload?.sourceDetails ?? []
            ),

            stR_SelectedSearchIds: actionMenuPayload?.stR_SelectedSearchIds,
          }}
          dataTestId="docList-action-menu"
          sourceTypeContext={SourceTypeContextCode.SEARCHTERMNOC}
          size="medium"
          subMenuDirection="left"
          direction="bottom-right"
          menuClassName="doc-list-action-menu"
          isDisabled={isActionBtnDisabled}
          selectedHitsDetails={actionMenuPayload?.sourceDetails}
          getSourceDetails={(sourceDetails, forAPI, forAction) => {
            const forActions = [
              ReportActionMenuOptionTypes.BulkTag,
              ReportActionMenuOptionTypes.BulkFolder,
              ReportActionMenuOptionTypes.QuickBatch,
              ReportActionMenuOptionTypes.BulkAssign,
              ReportActionMenuOptionTypes.BulkRelease,
            ];
            if (
              forAPI === 'getLikePopup' &&
              forActions.includes(forAction as ReportActionMenuOptionTypes)
            ) {
              return {...sourceDetails, LstBulkActionContext: []};
            } else if (
              forAPI === 'getBulkActionPopup' &&
              forActions.includes(forAction as ReportActionMenuOptionTypes)
            ) {
              return {...sourceDetails, LstBulkActionContext: []};
            }
            return sourceDetails;
          }}
          onActionPick={(
            action: CustomMenuItem,
            callBackForDoc?: (docId: string, source: string) => void
          ) => {
            handleActionSelect(action);
            onActionSelect?.(action, callBackForDoc);
          }}
        />
        {showDocLimitWarning && (
          <ArrDocMaxLimitWarning
            totalDocumentCount={totalSelectedCount}
            show={showDocLimitWarning}
            destination={navigationType}
            onCancel={() => setShowDocLimitWarning(false)}
            onContinue={(totalDocumentToView: number) =>
              startDocumentNavigation(navigationType, totalDocumentToView)
            }
          />
        )}
        {docViewNavigationLoading && (
          <ArrLoadingSpinner overlay isOpen={docViewNavigationLoading} />
        )}
      </div>
    </div>
  );
};

export default React.memo(SearchTermReportTotalCount);
